import { DateTime } from 'luxon';

/**
 * Canonical cross-surface timezone label: `GMT{offset} {City}`, e.g. "GMT+12 Auckland".
 * ONE format shared by the wizard picker, wizard summary, purchase page, and (mirrored on the
 * backend) the landing page + confirmation email, so no two surfaces disagree.
 *
 * The offset is DST-correct for whatever instant `dt` represents. Whole-hour offsets drop the
 * minutes ("GMT+12"); half/quarter-hour zones keep them ("GMT+9:30").
 */

function cityOf(iana) {
    return typeof iana === 'string' && iana ? iana.split('/').pop().replace(/_/g, ' ') : '';
}

function gmtOffset(dt) {
    const mins = dt.offset; // minutes east of UTC (can be negative)
    const sign = mins < 0 ? '-' : '+';
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `GMT${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
}

/**
 * Zone label at a specific Luxon DateTime instant (DST-correct for that instant).
 * @param {string} iana IANA zone.
 * @param {DateTime} [dt] A Luxon DateTime; if omitted/invalid, "now" is used.
 * @returns {string} e.g. "GMT+12 Auckland" (city alone if the offset can't resolve).
 */
export function formatZoneIndicator(iana, dt) {
    if (typeof iana !== 'string' || !iana.trim()) return '';
    const zoned = (dt && dt.isValid ? dt : DateTime.now()).setZone(iana);
    const city = cityOf(iana);
    if (!zoned.isValid) return city;
    return `${gmtOffset(zoned)} ${city}`;
}

/**
 * Zone label from a UTC ISO event instant — for surfaces holding a UTC start time
 * (purchase page). e.g. formatZoneFromISO('Pacific/Auckland', '2026-01-15T09:00:00Z').
 */
export function formatZoneFromISO(iana, utcIso) {
    return formatZoneIndicator(iana, utcIso ? DateTime.fromISO(utcIso, { zone: 'utc' }) : null);
}

/**
 * Zone option label for a picker row / trigger (current offset — the event date isn't known yet).
 * e.g. "GMT+12 Auckland".
 */
export function zoneOptionLabel(iana) {
    return formatZoneIndicator(iana, null);
}
